/**
 * Shiny gap tiles: a list of species (or regional forms) checked against the
 * Shiny Dex, drawn the Quick-update way. A shiny still missing is a
 * tap-to-choose gap tile (one button commits the lot, a committed tile taps
 * back), one already owned is a checked sprite, one GO has never released
 * shiny is a plain sprite (drawn only when the caller asks for the whole
 * pool). Shared by the Shiny Hunt block and the Rocket radar so the two
 * surfaces cannot drift (2026-09-08).
 *
 * FROZEN per `listKey`, the Quick-update rule: an added shiny stops being a
 * gap, so a live recompute would delete the tile just committed and its
 * undo. Pass a key that changes when the LIST changes (event id, feed
 * fetch time), never one that changes on a dex write.
 */
import { useMemo, type ReactNode } from "react";
import { Check } from "lucide-react";
import { CaughtSprite } from "@/components/CaughtSprite";
import { CatchUpTile, GAP_TILE_CELL_STYLE, GAP_TILE_ROW_CLASS } from "@/components/EventCatchUp";
import { useGapCommit } from "@/hooks/useGapCommit";
import { getDexEntryState } from "@/lib/dexStateStore";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { speciesFormArtCandidates } from "@/lib/formSpriteCandidates";
import { FORM_GROUPS_BY_FORM_ID } from "@/lib/generated/pokemonForms.generated";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { speciesName } from "@/data/speciesNames";
import type { RaidDexGap } from "@/lib/raidDexCrossRef";

export interface ShinyTileTarget {
  speciesId: number;
  /** The app's form id for a regional form; the tile draws and writes that form. */
  formId?: string;
}

export type ShinyTileStatus = "gap" | "owned" | "no-shiny";

/** The Shiny Dex's answer for one target. */
export function shinyTileStatus(t: ShinyTileTarget): ShinyTileStatus {
  if (getDexEntryState("shiny", t.speciesId, t.formId ?? null)?.state === "present") return "owned";
  const available = t.formId
    ? isEventFormAvailable("shiny", t.formId, t.speciesId)
    : isVariantAvailable("shiny", t.speciesId);
  return available ? "gap" : "no-shiny";
}

export function shinyTargetName(t: ShinyTileTarget): string {
  if (t.formId) {
    const form = FORM_GROUPS_BY_FORM_ID.get(t.formId)?.forms.find((f) => f.formId === t.formId);
    if (form?.displayName) return `${speciesName(t.speciesId)} (${form.displayName})`;
  }
  return speciesName(t.speciesId);
}

function targetKey(t: ShinyTileTarget): string {
  return `${t.speciesId}:${t.formId ?? ""}`;
}

/** A sprite that is not a gap: owned (checked, shiny art) or shiny-less (plain). */
export function ShinyStaticTile({
  target,
  owned,
  testId,
}: {
  target: ShinyTileTarget;
  owned: boolean;
  testId: string;
}) {
  const name = shinyTargetName(target);
  return (
    <div
      className="relative w-full rounded-xl p-1 flex flex-col items-center"
      style={{
        background: "var(--surface-raised)",
        border: owned ? "1px solid hsl(var(--shiny) / 0.45)" : "1px solid var(--border-subtle)",
        opacity: owned ? 0.85 : 0.55,
      }}
      data-testid={testId}
      data-status={owned ? "owned" : "no-shiny"}
      aria-label={owned ? `Shiny ${name}, already in your Nexus Dex` : `${name}, no shiny in GO yet`}
    >
      <span className="relative block w-full aspect-square">
        <CaughtSprite
          id={target.speciesId}
          fill
          shiny={owned}
          formSpriteCandidates={speciesFormArtCandidates(target.speciesId, target.formId, { shiny: owned })}
        />
        {owned && (
          <span
            className="absolute top-0 right-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full"
            style={{ background: "hsl(var(--shiny))", color: "#1a1200" }}
            aria-hidden
          >
            <Check className="w-2 h-2" />
          </span>
        )}
      </span>
      <span className="mt-0.5 w-full truncate text-center text-[7px] leading-tight text-[var(--text-tertiary)]">
        {name}
      </span>
    </div>
  );
}

/**
 * The strip: gap tiles first, then (unless `gapsOnly`) the owned and
 * shiny-less ones, one commit button for everything chosen, an undo line
 * after a commit. Renders nothing when there is nothing to draw.
 */
export function ShinyGapTiles({
  listKey,
  targets,
  heading,
  gapsOnly = false,
  testIdPrefix,
  emptyLine,
  captionFor,
}: {
  listKey: string;
  targets: readonly ShinyTileTarget[];
  /** Small uppercase line above the tiles; null for the default "still
   * missing shiny · tap to choose" when there are gaps, false for none at
   * all (the caller's own heading already says it). */
  heading?: string | null | false;
  /** Draw only the gaps (a radar), not the owned / shiny-less sprites. */
  gapsOnly?: boolean;
  testIdPrefix: string;
  /** Shown instead of nothing when every target is owned or shiny-less. */
  emptyLine?: string;
  /** A tiny identifier UNDER a tile naming where it comes from: a string
   * ("Giovanni", "1★ raid") or a stacked node (type disc / Female / Grunt). */
  captionFor?: (t: ShinyTileTarget) => ReactNode;
}) {
  const { gaps, others } = useMemo(() => {
    const gaps: RaidDexGap[] = [];
    const others: Array<{ target: ShinyTileTarget; owned: boolean }> = [];
    const seen = new Set<string>();
    for (const t of targets) {
      const k = targetKey(t);
      if (seen.has(k)) continue;
      seen.add(k);
      const status = shinyTileStatus(t);
      if (status === "gap") {
        gaps.push({
          speciesId: t.speciesId,
          name: shinyTargetName(t),
          variant: "shiny",
          ...(t.formId ? { formId: t.formId } : {}),
        });
      } else if (!gapsOnly) {
        others.push({ target: t, owned: status === "owned" });
      }
    }
    return { gaps, others };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);
  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(gaps);

  if (gaps.length === 0 && others.length === 0) {
    return emptyLine ? (
      <p className="mt-1 text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid={`${testIdPrefix}-empty`}>
        {emptyLine}
      </p>
    ) : null;
  }

  const caption = (t: ShinyTileTarget) => {
    const node = captionFor?.(t);
    return node ? (
      <div
        className="mt-0.5 w-full text-center text-[8px] font-bold uppercase tracking-[0.12em] leading-tight"
        style={{ color: "var(--text-tertiary)" }}
        data-testid="shiny-tile-caption"
      >
        {node}
      </div>
    ) : null;
  };

  return (
    <div data-testid={`${testIdPrefix}-tiles`} data-gaps={gaps.length}>
      {heading ? (
        <p className="mt-2 text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5">
          {heading}
        </p>
      ) : heading !== false && gaps.length > 0 ? (
        <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5">
          Still missing shiny · tap to choose
        </p>
      ) : null}
      <ul className={`${heading || gaps.length > 0 ? "" : "mt-2 "}${GAP_TILE_ROW_CLASS}`}>
        {gaps.map((g) => (
          <li key={`gap-${g.speciesId}-${g.formId ?? ""}`} className="min-w-0" style={GAP_TILE_CELL_STYLE}>
            <CatchUpTile
              variant="shiny"
              speciesId={g.speciesId}
              formId={g.formId}
              state={tileState(g)}
              onToggle={() => tap(g)}
              testId={`${testIdPrefix}-gap-${g.speciesId}${g.formId ? `-${g.formId}` : ""}`}
            />
            {caption(g)}
          </li>
        ))}
        {others.map(({ target, owned }) => (
          <li key={`own-${targetKey(target)}`} className="min-w-0" style={GAP_TILE_CELL_STYLE}>
            <ShinyStaticTile
              target={target}
              owned={owned}
              testId={`${testIdPrefix}-${owned ? "owned" : "plain"}-${target.speciesId}${target.formId ? `-${target.formId}` : ""}`}
            />
            {caption(target)}
          </li>
        ))}
      </ul>

      {selectedCount > 0 && (
        <button
          type="button"
          onClick={commit}
          data-testid={`${testIdPrefix}-commit`}
          className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
          style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)", minHeight: 40 }}
        >
          Add {selectedCount} to my Shiny Dex
        </button>
      )}
      {selectedCount === 0 && addedCount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
            {addedCount} added. Tap one to take it back.
          </p>
          <button
            type="button"
            onClick={undoAll}
            data-testid={`${testIdPrefix}-undo`}
            className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
              minHeight: 32,
            }}
          >
            Undo all
          </button>
        </div>
      )}
    </div>
  );
}
